/**
 * ESC/POS Direct Printer — USB + Network (LAN) printing for thermal printers.
 * Supports: USB (WebUSB), Network/LAN (TCP via backend proxy), and browser fallback.
 * Optimized for EPSON ESC/POS compatible 80mm printers.
 */

const ESC = 0x1B;
const GS = 0x1D;

const enc = new TextEncoder();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ═══════════════════════════════════════════════════════════════
// ESC/POS PRINTER CLASS — USB DIRECT
// ═══════════════════════════════════════════════════════════════
export class EscPosPrinter {
  constructor() {
    this.device = null;
    this.interfaceNumber = null;
    this.endpointOut = null;
    this.mode = 'usb'; // 'usb' or 'network'
    this.networkIp = null;
  }

  static isSupported() {
    return !!navigator.usb;
  }

  async connect(existingDevice = null) {
    try {
      if (existingDevice) {
        this.device = existingDevice;
      } else {
        this.device = await navigator.usb.requestDevice({
          filters: [
            { classCode: 7 },
            { vendorId: 0x0483 }, { vendorId: 0x0416 }, { vendorId: 0x1504 },
            { vendorId: 0x04B8 }, { vendorId: 0x0519 },
            { vendorId: 0x1FC9 }, { vendorId: 0x0DD4 },
            { vendorId: 0x20D1 }, { vendorId: 0x0FE6 },
          ]
        });
      }

      await this.device.open();

      const config = this.device.configuration;
      if (!config) await this.device.selectConfiguration(1);

      // Find interface and OUT endpoint
      this._findEndpoint();

      if (this.interfaceNumber === null || this.endpointOut === null) {
        throw new Error('Endpoint imprimante non trouve. Verifiez le cable USB.');
      }

      // Aggressive claim: try multiple strategies
      await this._claimWithRetry();
      this.mode = 'usb';
      return true;
    } catch (e) {
      console.error('USB connect error:', e);
      throw e;
    }
  }

  _findEndpoint() {
    this.interfaceNumber = null;
    this.endpointOut = null;

    // Strategy 1: Find printer class (7) or vendor-specific (255)
    for (const iface of this.device.configuration.interfaces) {
      for (const alt of iface.alternates) {
        if (alt.interfaceClass === 7 || alt.interfaceClass === 255) {
          for (const ep of alt.endpoints) {
            if (ep.direction === 'out') {
              this.interfaceNumber = iface.interfaceNumber;
              this.endpointOut = ep.endpointNumber;
              return;
            }
          }
        }
      }
    }

    // Strategy 2: Any interface with OUT endpoint
    for (const iface of this.device.configuration.interfaces) {
      for (const alt of iface.alternates) {
        for (const ep of alt.endpoints) {
          if (ep.direction === 'out') {
            this.interfaceNumber = iface.interfaceNumber;
            this.endpointOut = ep.endpointNumber;
            return;
          }
        }
      }
    }
  }

  async _claimWithRetry() {
    const strategies = [
      // Strategy 1: Direct claim
      async () => { await this.device.claimInterface(this.interfaceNumber); },
      // Strategy 2: Reset then claim
      async () => {
        await this.device.reset();
        await new Promise(r => setTimeout(r, 500));
        await this.device.claimInterface(this.interfaceNumber);
      },
      // Strategy 3: Close, reopen, select config, claim
      async () => {
        await this.device.close();
        await new Promise(r => setTimeout(r, 1000));
        await this.device.open();
        await this.device.selectConfiguration(1);
        await new Promise(r => setTimeout(r, 300));
        await this.device.claimInterface(this.interfaceNumber);
      },
      // Strategy 4: Reset, close, reopen with longer delay
      async () => {
        try { await this.device.reset(); } catch {}
        try { await this.device.close(); } catch {}
        await new Promise(r => setTimeout(r, 2000));
        await this.device.open();
        if (!this.device.configuration) await this.device.selectConfiguration(1);
        this._findEndpoint();
        await new Promise(r => setTimeout(r, 500));
        await this.device.claimInterface(this.interfaceNumber);
      },
    ];

    for (let i = 0; i < strategies.length; i++) {
      try {
        await strategies[i]();
        console.log(`USB claim succeeded with strategy ${i + 1}`);
        return;
      } catch (e) {
        console.warn(`USB claim strategy ${i + 1} failed:`, e.message);
        if (i === strategies.length - 1) {
          throw new Error(
            'Impossible de prendre le controle de l\'imprimante USB. ' +
            'Le systeme Android bloque l\'acces.\n\n' +
            'Solutions:\n' +
            '1. Redemarrez la tablette/POS\n' +
            '2. Debranchez et rebranchez le cable USB\n' +
            '3. Utilisez l\'impression reseau (IP: 192.168.1.23)\n' +
            '4. Fermez TOUTES les applications en arriere-plan'
          );
        }
      }
    }
  }

  async sendRaw(data) {
    if (!this.device || !this.endpointOut) throw new Error('Imprimante non connectee');
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    const chunkSize = 64;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.slice(i, i + chunkSize);
      await this.device.transferOut(this.endpointOut, chunk);
    }
  }

  async init() { await this.sendRaw([ESC, 0x40]); }
  async printLine(text = '') { await this.sendRaw(enc.encode(text + '\n')); }
  async align(pos) { await this.sendRaw([ESC, 0x61, pos]); }
  async bold(on) { await this.sendRaw([ESC, 0x45, on ? 1 : 0]); }
  async textSize(n) { await this.sendRaw([GS, 0x21, n]); }
  async separator(char = '-') { await this.printLine(char.repeat(48)); }
  async doubleSeparator() { await this.separator('='); }
  async feed(n = 1) { await this.sendRaw([ESC, 0x64, n]); }
  async cut() { await this.feed(3); await this.sendRaw([GS, 0x56, 0x00]); }
  async partialCut() { await this.feed(3); await this.sendRaw([GS, 0x56, 0x01]); }
  async openDrawer() { await this.sendRaw([ESC, 0x70, 0x00, 0x19, 0xFA]); }

  async printColumns(left, right, width = 48) {
    const space = width - left.length - right.length;
    await this.printLine(left + ' '.repeat(Math.max(1, space)) + right);
  }

  async disconnect() {
    try {
      if (this.device && this.interfaceNumber !== null) await this.device.releaseInterface(this.interfaceNumber);
      if (this.device) await this.device.close();
    } catch {}
    this.device = null;
    this.interfaceNumber = null;
    this.endpointOut = null;
  }

  static async getPairedDevices() {
    if (!navigator.usb) return [];
    try { return await navigator.usb.getDevices(); } catch { return []; }
  }
}


// ═══════════════════════════════════════════════════════════════
// NETWORK PRINTER CLASS — Sends ESC/POS via backend TCP proxy
// ═══════════════════════════════════════════════════════════════
export class NetworkPrinter {
  constructor(ip, port = 9100) {
    this.ip = ip;
    this.port = port;
    this.buffer = [];
    this.mode = 'network';
  }

  async init() { this.buffer = [[ESC, 0x40]]; }
  _push(data) { this.buffer.push(Array.isArray(data) ? data : Array.from(enc.encode(data))); }
  async printLine(text = '') { this._push(text + '\n'); }
  async align(pos) { this._push([ESC, 0x61, pos]); }
  async bold(on) { this._push([ESC, 0x45, on ? 1 : 0]); }
  async textSize(n) { this._push([GS, 0x21, n]); }
  async separator(char = '-') { this._push(char.repeat(48) + '\n'); }
  async doubleSeparator() { await this.separator('='); }
  async feed(n = 1) { this._push([ESC, 0x64, n]); }
  async cut() { await this.feed(3); this._push([GS, 0x56, 0x00]); }
  async partialCut() { await this.feed(3); this._push([GS, 0x56, 0x01]); }
  async openDrawer() { this._push([ESC, 0x70, 0x00, 0x19, 0xFA]); }

  async printColumns(left, right, width = 48) {
    const space = width - left.length - right.length;
    this._push(left + ' '.repeat(Math.max(1, space)) + right + '\n');
  }

  /** Flush all buffered data to printer via backend proxy */
  async flush() {
    const allBytes = [];
    for (const chunk of this.buffer) {
      if (Array.isArray(chunk)) allBytes.push(...chunk);
      else allBytes.push(...Array.from(enc.encode(String(chunk))));
    }
    this.buffer = [];

    const b64 = btoa(String.fromCharCode(...allBytes));
    const res = await fetch(`${API}/admin/printer/network-print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ip: this.ip, port: this.port, data_b64: b64 }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Erreur impression reseau (${res.status})`);
    }
    return true;
  }

  async disconnect() { this.buffer = []; }
}


// ═══════════════════════════════════════════════════════════════
// HIGH-LEVEL FUNCTIONS — Auto-detect USB or Network
// ═══════════════════════════════════════════════════════════════

let _printer = null;

export async function getConnectedPrinter() {
  if (_printer && (_printer.device || _printer.mode === 'network')) return _printer;

  // Try USB first
  if (EscPosPrinter.isSupported()) {
    const p = new EscPosPrinter();
    const devices = await EscPosPrinter.getPairedDevices();
    if (devices.length > 0) {
      try {
        await p.connect(devices[0]);
        _printer = p;
        return _printer;
      } catch { /* USB failed, will try network */ }
    }
  }

  return null;
}

export async function connectPrinter() {
  _printer = new EscPosPrinter();
  await _printer.connect();
  return _printer;
}

export async function connectNetworkPrinter(ip, port = 9100) {
  _printer = new NetworkPrinter(ip, port);
  return _printer;
}

export function isDirectPrintAvailable() {
  return EscPosPrinter.isSupported();
}

export function getActivePrinter() {
  return _printer;
}

export function clearPrinter() {
  _printer = null;
}


// ═══════════════════════════════════════════════════════════════
// TICKET PRINTING — Kitchen, Client, Closure, Test
// (Works with both USB and Network printers)
// ═══════════════════════════════════════════════════════════════

export async function directPrintTest(printer) {
  await printer.init();
  await printer.align(1);
  await printer.bold(true);
  await printer.textSize(17);
  await printer.printLine('TEST IMPRESSION');
  await printer.textSize(0);
  await printer.bold(false);
  await printer.printLine('');
  await printer.doubleSeparator();
  await printer.align(0);
  await printer.printLine('Protocole: ESC/POS');
  await printer.printLine(`Mode: ${printer.mode === 'network' ? 'Reseau (LAN)' : 'USB Direct'}`);
  if (printer.ip) await printer.printLine(`IP: ${printer.ip}`);
  await printer.printLine('Largeur: 80mm');
  await printer.printLine('');
  await printer.printLine('ABCDEFGHIJKLMNOPQRSTUVWXYZ');
  await printer.printLine('0123456789 !@#$%^&*()');
  await printer.printLine('');
  await printer.doubleSeparator();
  await printer.align(1);
  await printer.printLine('FIN DU TEST');
  await printer.doubleSeparator();
  await printer.cut();
  if (printer.flush) await printer.flush();
}

export async function directPrintKitchen(printer, order, items) {
  await printer.init();
  await printer.align(1);
  await printer.bold(true);
  await printer.textSize(17);
  await printer.printLine('BON DE CUISINE');
  await printer.textSize(0);
  await printer.bold(false);
  await printer.doubleSeparator();

  await printer.align(0);
  await printer.printColumns('Table:', String(order.table_id || '-'));
  await printer.printColumns('Type:', order.is_takeaway ? 'Emporter' : 'Sur place');
  await printer.printColumns('Heure:', new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));

  if (order.notes) {
    await printer.printLine('');
    await printer.bold(true);
    await printer.printLine('NOTE: ' + order.notes);
    await printer.bold(false);
  }

  await printer.separator();
  await printer.bold(true);
  await printer.printLine('QTE  ARTICLE');
  await printer.separator();
  await printer.bold(false);

  for (const item of items) {
    await printer.printLine(`${item.quantity}x   ${item.name}`);
    if (item.spice_level) await printer.printLine(`     Piment: ${item.spice_level}`);
  }

  await printer.doubleSeparator();
  await printer.align(1);
  await printer.bold(true);
  await printer.printLine(`${items.length} article(s)`);
  await printer.bold(false);
  await printer.cut();
  if (printer.flush) await printer.flush();
}

export async function directPrintReceipt(printer, order, items, fp) {
  const formatPrice = fp || ((p) => `${(p || 0).toLocaleString()} THB`);

  await printer.init();
  await printer.align(1);
  await printer.bold(true);
  await printer.textSize(17);
  await printer.printLine('HIBISUN');
  await printer.textSize(0);
  await printer.bold(false);
  await printer.printLine('Restaurant & Bar');
  await printer.printLine(new Date().toLocaleString('fr-FR'));
  await printer.doubleSeparator();

  await printer.align(0);
  await printer.printColumns('Table:', String(order.table_id || '-'));
  await printer.printColumns('Type:', order.is_takeaway ? 'Emporter' : 'Sur place');
  await printer.printColumns('Commande:', order.id?.slice(-6) || '-');

  await printer.separator();
  await printer.bold(true);
  await printer.printColumns('Article', 'Total');
  await printer.separator();
  await printer.bold(false);

  let total = 0;
  for (const item of items) {
    const lineTotal = (item.price || 0) * (item.quantity || 1);
    total += lineTotal;
    await printer.printColumns(`${item.quantity}x ${item.name}`, formatPrice(lineTotal));
  }

  await printer.doubleSeparator();
  await printer.bold(true);
  await printer.textSize(1);
  await printer.printColumns('TOTAL', formatPrice(order.total || total));
  await printer.textSize(0);
  await printer.bold(false);

  if (order.payment_method) {
    await printer.printColumns('Paiement:', order.payment_method.toUpperCase());
  }

  await printer.doubleSeparator();
  await printer.align(1);
  await printer.printLine('Merci de votre visite !');
  await printer.printLine('A bientot');
  await printer.cut();
  if (printer.flush) await printer.flush();
}

export async function directPrintClosure(printer, data, fp) {
  const formatPrice = fp || ((p) => `${(p || 0).toLocaleString()} THB`);

  await printer.init();
  await printer.align(1);
  await printer.bold(true);
  await printer.textSize(17);
  await printer.printLine('CLOTURE DE CAISSE');
  await printer.textSize(0);
  await printer.bold(false);
  await printer.printLine(new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
  await printer.printLine(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
  await printer.doubleSeparator();

  await printer.align(0);
  await printer.bold(true);
  await printer.printColumns('TOTAL COMMANDES', String(data.total_orders || 0));
  await printer.bold(false);

  await printer.separator();
  await printer.bold(true);
  await printer.textSize(1);
  await printer.printColumns("CHIFFRE D'AFFAIRES", formatPrice(data.total_revenue));
  await printer.textSize(0);
  await printer.bold(false);

  if (data.by_payment_method) {
    await printer.separator();
    await printer.bold(true);
    await printer.printLine('PAR MODE DE PAIEMENT');
    await printer.bold(false);
    for (const [method, info] of Object.entries(data.by_payment_method)) {
      await printer.printColumns(`${method} (${info.count})`, formatPrice(info.total));
    }
  }

  await printer.separator();
  await printer.bold(true);
  await printer.printLine('CONTROLE CAISSE');
  await printer.bold(false);
  await printer.printColumns('Fond de caisse', formatPrice(data.opening_cash || 0));
  await printer.printColumns('Cash encaisse', formatPrice(data.cash_revenue || 0));
  await printer.printColumns('Cash attendu', formatPrice(data.expected_cash || 0));
  await printer.printColumns('Cash compte', formatPrice(data.closing_cash_counted || 0));
  await printer.bold(true);
  const diff = data.cash_difference || 0;
  await printer.printColumns('ECART', `${diff >= 0 ? '+' : ''}${formatPrice(diff)}`);
  await printer.bold(false);

  await printer.doubleSeparator();
  await printer.align(1);
  await printer.printLine('--- Fin de cloture ---');
  await printer.cut();
  if (printer.flush) await printer.flush();
}
